<template>
  <div class="cal-range-picker">
    <!-- Selected range display row -->
    <div class="cal-range-picker__row">
      <div class="cal-range-picker__field">
        <span class="cal-range-picker__icon"><i class="fa-regular fa-calendar-alt"/></span>
        <span class="cal-range-picker__date">{{ formattedTime.start || msg('start') }}</span>
      </div>
      <span class="cal-range-picker__arrow"><i class="fa-solid fa-arrow-right"/></span>
      <div class="cal-range-picker__field">
        <span class="cal-range-picker__icon"><i class="fa-regular fa-calendar-alt"/></span>
        <span class="cal-range-picker__date">{{ formattedTime.end || msg('end') }}</span>
      </div>
    </div>
    <!-- Inline calendar (no popover) -->
    <v-date-picker class="cal-range-picker__vc" :masks="masks" v-bind="$props" v-model="valueDate"
                   is-range is-inline ref="picker" is24hr columns="2"/>
  </div>
</template>

<script>

export default {
  name: 'calendarPickerRange',
  data: () => ({
    masks: {
      L: 'yyyy-dd-MM',
      input: ['yyyy-dd-MM'],
      inputDateTime: ['yyyy-dd-MM HH:mm'],

      // ...optional `title`, `weekdays`, `navMonths`, etc
    }
  }),
  props: {
    value: {
      required: true,
      default: () => null
    },
    mode: {
      required: false,
      default: () => 'date'
    },
    size: {
      required: false,
      default: () => 'md'
    },
    minDate: {},
    maxDate: {},
    disabledDates: {},
    availableDates: {},
    clearable:{
      required: false,
      default: () => true
    }
  },
  computed: {
    valueDate: {
      get() {
        if (typeof this.value === 'object' && this.value !== null) {
          return this.value
        } else {
          return {
            start: null,
            end: null
          }
        }
      },
      set(val) {
        if (val.start != null) {
          val.start = this.$moment(val.start)
              .format(this.i18nInputKey)
        }
        if (val.end != null) {
          val.end = this.$moment(val.end)
              .format(this.i18nInputKey)
        }
        this.$emit('input', val)
      }
    },
    formattedTime() {
      let result = {
        start: '',
        end: ''
      }
      let startTime = this.$moment(this.valueDate.start, this.i18nInputKey)
      let endTime = this.$moment(this.valueDate.end, this.i18nInputKey)
      if (startTime.isValid()) {
        result.start = startTime.format('DD.MM.YYYY'); // Format to yyyy-MM-dd
      }
      if (endTime.isValid()) {
        result.end = endTime.format('DD.MM.YYYY'); // Format to yyyy-MM-dd
      }
      return result
    },
    i18nMaskKey() {
      return this.msg('timeFormat.' + this.mode + '.mask')
    },
    i18nFormatKey() {
      return this.msg('timeFormat.' + this.mode + '.format')
    },
    i18nInputKey() {
      return this.mode === 'date' ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm'
    },
  },
  methods: {
    setInput(val) {
      this.$emit('input', val)
    },
    clearInput() {
      this.$emit('input', null);
      this.forceUpdate();
    },
    setToToday() {
      let valueDate = {
        start: this.$moment().format(this.i18nInputKey),
        end: this.$moment().format(this.i18nInputKey)
      }
      this.valueDate = valueDate;
      this.forceUpdateValue(valueDate);
      this.moveCalendar(valueDate);
    },
    setThisMonth(){
      let valueDate = {
        start: this.$moment().startOf('month').format(this.i18nInputKey),
        end: this.$moment().format(this.i18nInputKey)
      }
      this.valueDate = valueDate;
      this.forceUpdateValue(valueDate);
      this.moveCalendar(valueDate);
    },
    setLastMonth(){
      let valueDate = {
        start: this.$moment().subtract(1, 'month').startOf('month').format(this.i18nInputKey),
        end: this.$moment().subtract(1, 'month').endOf('month').format(this.i18nInputKey)
      }

      this.valueDate = valueDate;
      this.moveCalendar(valueDate);
      this.forceUpdateValue(valueDate);
    },
    setDays(days){
      let valueDate = {
        start: this.$moment().subtract(days, 'days').format(this.i18nInputKey),
        end: this.$moment().format(this.i18nInputKey)
      }
      this.valueDate = valueDate;
      this.moveCalendar(valueDate);
      this.forceUpdateValue(valueDate);
    },
    setThisYear(){
      let valueDate = {
        start: this.$moment().startOf('year').format(this.i18nInputKey),
        end: this.$moment().format(this.i18nInputKey)
      }
      this.valueDate = valueDate;
      this.moveCalendar(valueDate);
      this.forceUpdateValue(valueDate);
    },
    forceUpdateValue(valueDate){
      this.$refs.picker.forceUpdateValue(valueDate);
    },
    moveCalendar(valueDate){
      let moment = this.$moment(valueDate.start,this.i18nInputKey);
      let month = parseInt(moment.format("M"));
      let year = parseInt(moment.format("YYYY"));
      this.$refs.picker.move({ month, year });
    }
  }
}
</script>

<style lang="scss" scoped>
.cal-range-picker { width: 100%; }
/* Selected range row */
.cal-range-picker__row { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.cal-range-picker__field {
  flex: 1; display: inline-flex; align-items: center;
  background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 10px; padding: 7px 12px; gap: 8px;
}
.cal-range-picker__icon { color: #7367f0; font-size: .9rem; flex-shrink: 0; }
.cal-range-picker__date { font-size: .875rem; font-weight: 600; color: #334155; }
.cal-range-picker__arrow { color: #94a3b8; font-size: .75rem; }
/* Inline calendar */
.cal-range-picker__vc { width: 100% !important; border: none !important; background: transparent !important; }
</style>
<style lang="scss">
/* v-calendar inline style fixes */
.cal-range-picker .vc-container { border: none !important; background: transparent !important; width: 100% !important; font-family: inherit !important; }
.cal-range-picker .vc-pane-container { width: 100% !important; }
.cal-range-picker .vc-pane { flex: 1; }
.cal-range-picker .vc-highlight { background: #7367f0 !important; border-radius: 8px !important; }
.cal-range-picker .vc-highlight-content-light { background: rgba(115,103,240,.15) !important; border-radius: 8px !important; }
.cal-range-picker .vc-day-content:hover { background: rgba(115,103,240,.12) !important; border-radius: 8px !important; }
.cal-range-picker .vc-title { font-weight: 700 !important; font-size: .92rem !important; }
.cal-range-picker .vc-weekday { font-weight: 700; font-size: .72rem; color: #94a3b8; text-transform: uppercase; }

/* dark */
.dark-layout .cal-range-picker__field { background: rgba(255,255,255,.06); border-color: rgba(255,255,255,.1); }
.dark-layout .cal-range-picker__icon { color: #a78bfa; }
.dark-layout .cal-range-picker__date { color: #e2e8f0; }
.dark-layout .cal-range-picker__arrow { color: #4b5563; }
.dark-layout .cal-range-picker .vc-container { background: transparent !important; }
.dark-layout .cal-range-picker .vc-title { color: #e2e8f0 !important; }
.dark-layout .cal-range-picker .vc-arrow { color: #94a3b8 !important; }
.dark-layout .cal-range-picker .vc-weekday { color: #4b5563 !important; }
.dark-layout .cal-range-picker .vc-day-content { color: #cbd5e1 !important; }
.dark-layout .cal-range-picker .vc-day-content:hover { background: rgba(115,103,240,.2) !important; color: #fff !important; }
.dark-layout .cal-range-picker .is-today .vc-day-content { color: #a78bfa !important; font-weight: 700; }
.dark-layout .cal-range-picker .vc-highlight-content-solid { color: #fff !important; }
.dark-layout .cal-range-picker .vc-highlight-content-light { color: #a78bfa !important; }
</style>
